// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the meeting picker's line about people (MailMeetingPicker): who is free, busy or can't be
// seen at a time, e.g. "You, Berno and Markus are free · Can't see Ben's calendar".

import * as M from '@app/features/konvo_mail/components/MailMeetingStrings';
import type {MailMeetingAction, MeetingOption} from '@app/features/konvo_mail/state/MailMeeting';
import type {I18n} from '@lingui/core';

function listOf(names: Array<string>, locale: string): string {
	try {
		return new Intl.ListFormat(locale, {style: 'long', type: 'conjunction'}).format(names);
	} catch {
		return names.join(', ');
	}
}

/** "You, Berno and Markus are free · Can't see Ben's calendar" for one time. */
export function peopleLine(
	i18n: I18n,
	action: MailMeetingAction,
	option: MeetingOption | null,
	locale: string,
): string {
	const name = (index: number) => {
		const person = action.people[index]!;
		return person.self ? i18n._(M.YOU_DESCRIPTOR) : person.name;
	};
	const known = action.people.map((person, index) => ({person, index})).filter((entry) => entry.person.known);
	const busy = option ? known.filter((entry) => option.busy.includes(entry.index)) : [];
	const free = option ? known.filter((entry) => !option.busy.includes(entry.index)) : [];
	const unknown = action.people.map((person, index) => ({person, index})).filter((entry) => !entry.person.known);
	const parts: Array<string> = [];
	const describe = (
		entries: Array<{person: {self: boolean}; index: number}>,
		selfOnly: Parameters<I18n['_']>[0],
		many: Parameters<I18n['_']>[0],
	) => {
		if (!entries.length) return;
		if (entries.length === 1 && entries[0]!.person.self) parts.push(i18n._(selfOnly));
		else
			parts.push(
				i18n._(many, {
					count: entries.length,
					names: listOf(
						entries.map((entry) => name(entry.index)),
						locale,
					),
				}),
			);
	};
	describe(free, M.YOU_FREE_DESCRIPTOR, M.NAMES_FREE_DESCRIPTOR);
	describe(busy, M.YOU_BUSY_DESCRIPTOR, M.NAMES_BUSY_DESCRIPTOR);
	if (unknown.length) {
		parts.push(
			i18n._(M.NAMES_UNKNOWN_DESCRIPTOR, {
				count: unknown.length,
				names: listOf(
					unknown.map((entry) => entry.person.name),
					locale,
				),
			}),
		);
	}
	return parts.join(' · ');
}
